
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Button } from 'react-bootstrap';
import { toast } from 'react-toastify';

// Layouts & Styles
import { StatusCard, StatusCardData, StatusCol, SelectSettingOptionDiv, StyledButton, SelectSettingDescriptionDiv, StyledRadio, StyledRadioInput, StyledRadioLabel } from './styles';
import {  ModalFooter } from '../CommonStyles';

// Constants
import { ROBOT_STATUS_DEFAULT_VALUE } from 'Config/websocketConstants';
import { ROBOT_STATUS_DASHBOARD_TEXT_COLORS } from 'Config/colors';
import { EXPOSURE_SETTINGS } from 'Config/zcamConstants';

// Components
import Modal from 'Components/Modal';

//
import { apiUpdateRobotZCamSingleSettingRequest } from 'Redux/actions/gear';

const RobotStatusDashboardColumn = ({
    onlineRobotAirIdsDisplayOrder,
    columnTitle,
    columnDataObj,
    liveStatusObj = null, // Recording status or Stream status or null for anything else
    vpnIpById = null,
    robotSettingsConstant = null,
    robotAirIdstoNickNames = null,
}) => {
    let dispatch = useDispatch();
    const auth = useSelector(state => state.auth);
    const [showModal, setShowModal] = useState(false);
    const [selectedRobotAirId, setSelectedRobotAirId] = useState('');
    const [selectedUpdateStrValue, setSelectedUpdateStrValue] = useState(null);
    const [selectedUpdateIntValue, setSelectedUpdateIntValue] = useState(0);

    const onClickBtn = (robotAirId) => {
        setSelectedRobotAirId(robotAirId);
        let curValue;
        if (robotSettingsConstant?.type === 1) { // type 1: choice; 2: range; 3: string
            if (robotAirId in columnDataObj) {
                if (robotSettingsConstant === EXPOSURE_SETTINGS.F_STOP) {
                    curValue = columnDataObj[robotAirId].substring(1);
                } else {
                    curValue = columnDataObj[robotAirId].toString();
                }
            } else {
               curValue = robotSettingsConstant?.defaultValue;
            }
            if (robotSettingsConstant?.options.includes(curValue)) {
                setSelectedUpdateStrValue(curValue);
            } else {
                setSelectedUpdateStrValue(robotSettingsConstant?.defaultValue);
            }
        } else {
            if (robotAirId in columnDataObj) {
                curValue = Number(columnDataObj[robotAirId]);
            } else {
               curValue = robotSettingsConstant?.defaultValue;
            }
            setSelectedUpdateIntValue(curValue);
        }
        setShowModal(true);
    };

    const onHideModal = () => {
        setSelectedRobotAirId('');
        if (robotSettingsConstant?.type === 1) {
            setSelectedUpdateStrValue(null);
        } else {
            setSelectedUpdateIntValue(robotSettingsConstant?.defaultValue);
        }
        setShowModal(false);
    };

    const onApplyBtnClick = () => {
        if (robotSettingsConstant?.type === 1 && selectedUpdateStrValue === null) {
            toast.error('Please choose a value before update the robot setting');
            return;
        }
        if (selectedRobotAirId in vpnIpById) {
            let robotIp = vpnIpById[selectedRobotAirId];
            let updateKey = robotSettingsConstant.key;
            if (robotSettingsConstant?.type === 1) {
                dispatch(apiUpdateRobotZCamSingleSettingRequest(
                    auth.token,
                    robotAirIdstoNickNames?.[selectedRobotAirId],
                    {
                        robot_ip: robotIp,
                        robot_air_id: selectedRobotAirId,
                        update_setting_title: robotSettingsConstant.title,
                        zcam_url_key: updateKey,
                        update_value: selectedUpdateStrValue,
                    }
                ));
            } else {
                dispatch(apiUpdateRobotZCamSingleSettingRequest(
                    auth.token,
                    robotAirIdstoNickNames?.[selectedRobotAirId],
                    {
                        robot_ip: robotIp,
                        robot_air_id: selectedRobotAirId,
                        update_setting_title: robotSettingsConstant.title,
                        zcam_url_key: updateKey,
                        update_value: selectedUpdateIntValue,
                    }
                ));
            }
        }
        onHideModal();
    };

    const RobotSettingsModalBody = (
        <SelectSettingOptionDiv>
            <SelectSettingDescriptionDiv>
                Selected Value: {robotSettingsConstant?.type === 1 ? selectedUpdateStrValue : selectedUpdateIntValue}
            </SelectSettingDescriptionDiv>
            {robotSettingsConstant?.type === 1 ? (
                robotSettingsConstant.options.map((option, index) => {
                    return (
                        <StyledRadio type="radio" name={robotSettingsConstant.key} key={option}>
                            <StyledRadioInput
                                checked={selectedUpdateStrValue === option}
                                onChange={(e) => setSelectedUpdateStrValue(option)}
                            />
                            <StyledRadioLabel>{option}</StyledRadioLabel>
                        </StyledRadio>
                    )
                })
            ) : (
                <Form.Control
                    type="range"
                    min={robotSettingsConstant?.min}
                    max={robotSettingsConstant?.max}
                    step={robotSettingsConstant?.step}
                    value={selectedUpdateIntValue}
                    onChange={(e) => setSelectedUpdateIntValue(e.target.value)}
                />
            )}
        </SelectSettingOptionDiv>
    );

    const RobotSettingsModalFooter = (
        <ModalFooter>
            <Button onClick={onHideModal} variant="secondary">
                Cancel
            </Button>
            <Button onClick={onApplyBtnClick} variant="primary">
                Apply
            </Button>
        </ModalFooter>
    );

    const checkSystemStatus = (curStatus, lowThreshold, highThreshold) => {
        const num = parseFloat(curStatus)
        if (isNaN(num)) {
            return [ROBOT_STATUS_DASHBOARD_TEXT_COLORS.GREEN];
        }

        if (num < lowThreshold) {
            return [ROBOT_STATUS_DASHBOARD_TEXT_COLORS.GREEN];
        } else if (num > highThreshold) {
            return [ROBOT_STATUS_DASHBOARD_TEXT_COLORS.RED];
        } else {
            return [ROBOT_STATUS_DASHBOARD_TEXT_COLORS.YELLOW];
        }
    };

    const getTextColor = (robotAirId) => {
        if (liveStatusObj != null) {
            if ((robotAirId in liveStatusObj) && (typeof liveStatusObj[robotAirId] === 'string') && (liveStatusObj[robotAirId].includes("START"))) {
                return [ROBOT_STATUS_DASHBOARD_TEXT_COLORS.RED];
            }
        } else if (columnTitle.includes('CPU') && (robotAirId in columnDataObj)) {
            switch (columnTitle) {
                case "CPU Load":
                    return checkSystemStatus(columnDataObj[robotAirId], 90, 100);
                case "CPU Temp":
                    return checkSystemStatus(columnDataObj[robotAirId], 55, 65);
                case "CPU Mem":
                    return checkSystemStatus(columnDataObj[robotAirId], 50, 80);
                case "CPU Disk":
                    return checkSystemStatus(columnDataObj[robotAirId], 50, 80);
                default:
                    return [ROBOT_STATUS_DASHBOARD_TEXT_COLORS.GREEN];
            }
        } else if (columnTitle === 'Card TRT' && (robotAirId in columnDataObj)) {
            if (typeof columnDataObj[robotAirId] === 'string') {
                const match = columnDataObj[robotAirId].match(/^(\d{1,2})h (\d{1,2})m$/); // Card TRT data format: 00h 00m

                if (match) {
                    const hours = parseInt(match[1], 10);
                    const minutes = parseInt(match[2], 10);
                    if (hours === 0 && minutes <= 15) {
                        return [ROBOT_STATUS_DASHBOARD_TEXT_COLORS.YELLOW];
                    }
                }
            }
        } else {
            return [ROBOT_STATUS_DASHBOARD_TEXT_COLORS.GREEN];
        }
        return [ROBOT_STATUS_DASHBOARD_TEXT_COLORS.GREEN];
    }
    return (
        <StatusCol>
            <StatusCard>
                <StatusCardData isTitle textColor={[ROBOT_STATUS_DASHBOARD_TEXT_COLORS.ORANGE]}>
                    {columnTitle}
                </StatusCardData>
            </StatusCard>
            {robotSettingsConstant !== null ? (
                onlineRobotAirIdsDisplayOrder.map((robotAirId, index) => {
                    return (
                        <StatusCard key={robotAirId}>
                            <StyledButton onClick={() => {onClickBtn(robotAirId)}} variant="dark">
                                <StatusCardData textColor={getTextColor(robotAirId)}>
                                    {columnDataObj[robotAirId] ?? ROBOT_STATUS_DEFAULT_VALUE}
                                </StatusCardData>
                            </StyledButton>
                        </StatusCard>
                    )
                })
            ) : (
                onlineRobotAirIdsDisplayOrder.map((robotAirId, index) => {
                    return (
                        <StatusCard key={robotAirId}>
                            <StatusCardData textColor={getTextColor(robotAirId)}>
                                {columnDataObj[robotAirId] ?? ROBOT_STATUS_DEFAULT_VALUE}
                            </StatusCardData>
                        </StatusCard>
                    )
                })
            )}
            <Modal
                show={showModal}
                onHide={onHideModal}
                title={`Update ${columnTitle} for: ${robotAirIdstoNickNames?.[selectedRobotAirId] ?? 'Unknown'}`}
                body={RobotSettingsModalBody}
                footer={RobotSettingsModalFooter}
            />
        </StatusCol>
    );
};

export default RobotStatusDashboardColumn;
